
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Code,
  Brain,
  Settings,
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  Mail
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Logo from './Logo';

const Navigation: React.FC = () => {
  const { state, logout } = useAuth();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => { setAvatarFailed(false); }, [state.user?.avatar]);
  useEffect(() => {
    document.documentElement.dataset.lbuPalette = 'green';
    window.localStorage.setItem('lbu-interface-palette', 'green');
  }, []);

  // `activePrefix` marks a top-level section (e.g. the HLD_Section) whose entry
  // stays active for any route beneath its base path, not just the exact path.
  const navItems: {
    id: string;
    label: string;
    path: string;
    icon: typeof Home;
    activePrefix?: string;
  }[] = [
      { id: 'home', label: 'Dashboard', path: '/', icon: Home },
      { id: 'system-design', label: 'System Design', path: '/system-design', icon: Settings },
      { id: 'languages', label: 'Languages', path: '/languages', icon: Code },
      // { id: 'hld-workspace', label: 'HLD Workspace', path: '/hld', icon: Network, activePrefix: '/hld' }, // Disabled for current deployment - will be enabled in next version
      { id: 'practice', label: 'Practice', path: '/practice', icon: Brain },
      { id: 'contact', label: 'Contact', path: '/contact', icon: Mail },
    ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const userMenuButton = document.querySelector('[data-user-menu-button]');
      const userMenuDropdown = document.querySelector('[data-user-menu-dropdown]');

      if (isUserMenuOpen &&
        userMenuButton &&
        userMenuDropdown &&
        !userMenuButton.contains(target) &&
        !userMenuDropdown.contains(target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const isItemActive = (item: { path: string; activePrefix?: string }) => {
    // Section entries with an activePrefix stay active for any nested route
    // (e.g. /hld, /hld/framework, /hld/workspace/HLD@...), so the HLD_Section
    // entry renders active whenever the current route belongs to the section
    // and every other entry renders inactive (Req 1.4).
    if (item.activePrefix) {
      return (
        location.pathname === item.activePrefix ||
        location.pathname.startsWith(`${item.activePrefix}/`)
      );
    }
    return location.pathname === item.path;
  };

  return (
    <nav className={`site-navigation fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
      ? 'glass-effect shadow-soft bg-white/90 dark:bg-gray-900/90 backdrop-blur-md'
      : 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Logo size="lg" className="space-x-3" />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${isItemActive(item)
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* User Profile / Auth Buttons */}
            {state.isAuthenticated ? (
              <div className="hidden sm:block relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  data-user-menu-button
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-all duration-200"
                >
                  {state.user?.avatar && !avatarFailed ? (
                    <img
                      src={state.user.avatar}
                      alt={state.user.fullName}
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                      className="w-9 h-9 rounded-full object-cover"
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                      {state.user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <div className="hidden lg:flex flex-col items-start leading-tight">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[8rem]">
                      {state.user?.fullName}
                    </span>
                    {/* DISABLED: Subscription plan — uncomment when subscription feature is ready
                    <span className="text-[11px] text-gray-600 dark:text-gray-400 capitalize">
                      {state.user?.subscription || 'free'} plan
                    </span>
                    */}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div data-user-menu-dropdown className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 animate-slide-up origin-top-right">
                    {/* Account header */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      {state.user?.avatar && !avatarFailed ? (
                        <img
                          src={state.user.avatar}
                          alt={state.user.fullName}
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          className="w-10 h-10 rounded-full object-cover"
                          onError={() => setAvatarFailed(true)}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold shadow-sm">
                          {state.user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {state.user?.fullName}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {state.user?.email}
                        </p>
                      </div>
                    </div>

                    {/* DISABLED: Plan badge — uncomment when subscription feature is ready
                    <div className="px-4 pb-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 capitalize">
                        {state.user?.subscription || 'free'} plan
                      </span>
                    </div>
                    */}

                    <div className="border-t border-gray-100 dark:border-gray-700 mb-1" />

                    <Link
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center space-x-3">
                <Link
                  to="/login"
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden animate-slide-up">
            <div className="px-2 pt-2 pb-3 space-y-1 border-t border-gray-200 dark:border-gray-700">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center space-x-3 ${isItemActive(item)
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              {/* Mobile Auth Section */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                {state.isAuthenticated ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      {state.user?.avatar && !avatarFailed ? (
                        <img
                          src={state.user.avatar}
                          alt={state.user.fullName}
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          className="w-8 h-8 rounded-full"
                          onError={() => setAvatarFailed(true)}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold text-sm">
                          {state.user?.fullName?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {state.user?.fullName}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          View Profile
                        </p>
                      </div>
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 mt-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Sign Out</span>
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-3 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-3 py-2 rounded-lg bg-primary-600 text-white font-medium text-center"
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
